import React, { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { wsUrl } from "../lib/api";
import { useAuth } from "./AuthContext";

const WSContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
    const { user } = useAuth();
    const [lastEvent, setLastEvent] = useState(null);
    const wsRef = useRef(null);
    const listenersRef = useRef(new Set());

    useEffect(() => {
        if (!user) return;
        let alive = true;
        let reconnectTimeout;

        const connect = () => {
            try {
                // Aggiungiamo user_id come query param: il backend lo usa per inviare
                // print_problem mirati solo al terminale che ha fatto la comanda.
                const base = wsUrl();
                const sep = base.includes("?") ? "&" : "?";
                const url = `${base}${sep}user_id=${encodeURIComponent(user.id)}`;
                const ws = new WebSocket(url);
                wsRef.current = ws;
                ws.onmessage = (ev) => {
                    try {
                        const msg = JSON.parse(ev.data);
                        setLastEvent({ ...msg, _t: Date.now() });
                        listenersRef.current.forEach((cb) => cb(msg));
                    } catch (_e) {
                        // ignore
                    }
                };
                ws.onclose = () => {
                    if (alive) {
                        reconnectTimeout = setTimeout(connect, 2000);
                    }
                };
                ws.onerror = () => {
                    try { ws.close(); } catch (_e) { /* */ }
                };
            } catch (_e) {
                if (alive) reconnectTimeout = setTimeout(connect, 3000);
            }
        };
        connect();

        return () => {
            alive = false;
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            try { wsRef.current?.close(); } catch (_e) { /* */ }
        };
    }, [user]);

    // ⚠️ `subscribe` STABILE (useCallback senza dipendenze): consumer come BrowserPrintAgent
    // possono usarla in useEffect senza ri-sottoscriversi a OGNI messaggio (race che faceva
    // perdere stampe durante invii rapidi).
    const subscribe = useCallback((cb) => {
        listenersRef.current.add(cb);
        return () => listenersRef.current.delete(cb);
    }, []);

    // ⚠️ Memoizziamo il value SOLO sui field davvero usati. `subscribe` è stabile;
    // `lastEvent` cambia ad ogni msg ma è separato dagli altri.
    const value = useMemo(() => ({ lastEvent, subscribe }), [lastEvent, subscribe]);

    return (
        <WSContext.Provider value={value}>
            {children}
        </WSContext.Provider>
    );
};

export const useWS = () => useContext(WSContext);
